/**
 * Polyfills para compatibilidad con navegadores móviles antiguos
 * iOS Safari 12+, Chrome 80+, Firefox 78+, Samsung Internet 10+
 */

// Polyfill para IntersectionObserver (iOS Safari < 12.2)
if (typeof window !== "undefined" && !("IntersectionObserver" in window)) {
  console.log("⚠️ IntersectionObserver no soportado, cargando polyfill...");
  import("intersection-observer").catch(() => {
    console.warn("No se pudo cargar IntersectionObserver polyfill");
  });
}

// Polyfill para requestIdleCallback (Safari no lo soporta nativamente)
if (typeof window !== "undefined") {
  window.requestIdleCallback =
    window.requestIdleCallback ||
    function (handler) {
      let startTime = Date.now();
      return setTimeout(function () {
        handler({
          didTimeout: false,
          timeRemaining: function () {
            return Math.max(0, 50.0 - (Date.now() - startTime));
          },
        });
      }, 1);
    };

  window.cancelIdleCallback =
    window.cancelIdleCallback ||
    function (id) {
      clearTimeout(id);
    };
}

// Polyfill para Promise.allSettled (iOS Safari < 13)
if (typeof Promise !== "undefined" && !Promise.allSettled) {
  Promise.allSettled = function (promises) {
    return Promise.all(
      promises.map((promise) =>
        Promise.resolve(promise)
          .then((value) => ({ status: "fulfilled", value }))
          .catch((reason) => ({ status: "rejected", reason }))
      )
    );
  };
}

// Polyfill para Object.fromEntries (iOS Safari < 12.1)
if (typeof Object.fromEntries !== "function") {
  Object.fromEntries = function (entries) {
    if (!entries || !entries[Symbol.iterator]) {
      throw new Error("Object.fromEntries() requires an iterable argument");
    }
    const obj = {};
    for (const [key, value] of entries) {
      obj[key] = value;
    }
    return obj;
  };
}

// Polyfill para String.prototype.replaceAll (iOS Safari < 13.7)
if (typeof String.prototype.replaceAll !== "function") {
  String.prototype.replaceAll = function (search, replacement) {
    if (search instanceof RegExp) {
      return this.replace(
        new RegExp(
          search.source,
          "g" + (search.ignoreCase ? "i" : "") + (search.multiline ? "m" : "")
        ),
        replacement
      );
    }
    return this.split(search).join(replacement);
  };
}

// Polyfill para navigator.vibrate (algunos navegadores Android antiguos)
if (typeof navigator !== "undefined" && !navigator.vibrate) {
  navigator.vibrate =
    navigator.webkitVibrate ||
    navigator.mozVibrate ||
    navigator.msVibrate ||
    function () {
      return false;
    };
}

// Polyfill para fetch con timeout (mejorar estabilidad en redes lentas)
if (typeof window !== "undefined" && window.fetch) {
  const originalFetch = window.fetch;
  window.fetch = function (url, options = {}) {
    const timeout = options.timeout || 30000;

    return Promise.race([
      originalFetch(url, options),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Request timeout")), timeout)
      ),
    ]);
  };
}

// Soporte para CSS.supports (feature detection)
if (typeof window !== "undefined" && !window.CSS) {
  window.CSS = { supports: () => false };
} else if (typeof window !== "undefined" && !window.CSS.supports) {
  window.CSS.supports = () => false;
}

// Polyfill para Element.closest (Android < 5)
if (typeof Element !== "undefined" && !Element.prototype.closest) {
  Element.prototype.closest = function (selector) {
    let el = this;
    while (el) {
      if (el.matches(selector)) return el;
      el = el.parentElement;
    }
    return null;
  };
}

// Polyfill para Element.matches (Android < 5)
if (typeof Element !== "undefined" && !Element.prototype.matches) {
  Element.prototype.matches =
    Element.prototype.matchesSelector ||
    Element.prototype.mozMatchesSelector ||
    Element.prototype.msMatchesSelector ||
    Element.prototype.oMatchesSelector ||
    Element.prototype.webkitMatchesSelector ||
    function (s) {
      const matches = (this.document || this.ownerDocument).querySelectorAll(s);
      let i = matches.length;
      while (--i >= 0 && matches.item(i) !== this) {}
      return i > -1;
    };
}

// Polyfill para Array.prototype.flat (iOS Safari < 12)
if (!Array.prototype.flat) {
  Array.prototype.flat = function (depth = 1) {
    const flatten = (arr, depth) => {
      if (depth < 1) return arr.slice();
      return arr.reduce((acc, val) => {
        return acc.concat(Array.isArray(val) ? flatten(val, depth - 1) : val);
      }, []);
    };
    return flatten(this, depth);
  };
}

// Soporte para passive event listeners con feature detection
let supportsPassive = false;
if (typeof window !== "undefined") {
  try {
    const opts = Object.defineProperty({}, "passive", {
      get: function () {
        supportsPassive = true;
        return true;
      },
    });
    window.addEventListener("testPassive", null, opts);
    window.removeEventListener("testPassive", null, opts);
  } catch (e) {}
}

// Exportar para uso en otros módulos
export const SUPPORTS_PASSIVE = supportsPassive;
export const SUPPORTS_WEBP = (() => {
  if (typeof document === "undefined") return false;
  const canvas = document.createElement("canvas");
  if (canvas.getContext && canvas.getContext("2d")) {
    return canvas.toDataURL("image/webp").indexOf("data:image/webp") === 0;
  }
  return false;
})();

export const SUPPORTS_TOUCH =
  typeof window !== "undefined" &&
  ("ontouchstart" in window || navigator.maxTouchPoints > 0);

export const IS_IOS =
  typeof window !== "undefined" &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !window.MSStream;

export const IS_ANDROID =
  typeof window !== "undefined" && /Android/.test(navigator.userAgent);

export const IS_SAFARI =
  typeof window !== "undefined" &&
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

// Log de información del navegador
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  console.log("🌐 Información del navegador:", {
    userAgent: navigator.userAgent,
    supportsPassive: SUPPORTS_PASSIVE,
    supportsWebP: SUPPORTS_WEBP,
    supportsTouch: SUPPORTS_TOUCH,
    isIOS: IS_IOS,
    isAndroid: IS_ANDROID,
    isSafari: IS_SAFARI,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
  });
}
